import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/apiClient';
import Breadcrumb from '../ui/Breadcrumb';
import { moduleColor } from '../ui/modulePalette';

const MODULE_LABELS = {
  dashboard: 'Dashboard',
  traffic: 'Trafic Web',
  articles: 'Articles',
  projects: 'Projets',
  updates: 'Mises à jour',
  settings: 'Paramètres',
  backups: 'Sauvegarde',
  appointments: 'Rendez-vous',
  menus: 'Menus',
  scripts: 'Scripts',
  team: 'Équipe',
  languages: 'Langues',
  accessibility: 'Accessibilité',
  resources: 'Ressources',
  databases: 'Bases de données',
  gallery: 'Galerie',
  clients: 'Contacts',
  sites: 'Sites',
  employees: 'Employés',
  logs: 'Logs',
};

function moduleLabel(key) {
  const k = String(key || '');
  return MODULE_LABELS[k] || k || 'Module';
}

function userLabel(u) {
  const name = String(u?.display_name || '').trim();
  if (name) return name;
  const email = String(u?.email || '').trim();
  if (email) return email;
  const id = Number(u?.id || 0);
  return id > 0 ? `Utilisateur #${id}` : 'Utilisateur';
}

const MODULE_OBJECTS = {
  projects: 'un projet',
  articles: 'un article',
  gallery: 'un élément de galerie',
  menus: 'un menu',
  scripts: 'un script',
  updates: 'une mise à jour',
  appointments: 'un rendez-vous',
  traffic: 'la configuration trafic',
  resources: 'une ressource',
  languages: 'une langue',
  team: 'un membre',
  databases: 'une connexion BDD',
  settings: 'un paramètre',
  clients: 'un contact',
  sites: 'un site',
  employees: 'un employé',
  logs: 'un log',
};

function baseActionPhrase(actionKey) {
  const a = String(actionKey || '').toLowerCase();
  if (!a) return 'a effectué une action';
  if (a === 'update_modules') return 'a mis à jour les modules du site';
  if (a.startsWith('create') || a.startsWith('post')) return 'a créé';
  if (a.startsWith('update') || a.startsWith('put') || a.startsWith('patch')) return 'a modifié';
  if (a.startsWith('delete') || a.startsWith('remove')) return 'a supprimé';
  if (a.includes('enable') || a.includes('activate')) return 'a activé';
  if (a.includes('disable') || a.includes('deactivate')) return 'a désactivé';
  if (a.includes('publish')) return 'a publié';
  if (a.includes('archive')) return 'a archivé';
  if (a.includes('ensure')) return 'a initialisé';
  if (a.includes('test')) return 'a testé';
  if (a.includes('run')) return 'a exécuté';
  if (a.includes('reorder') || a.includes('sort')) return 'a réorganisé';
  if (a.includes('duplicate')) return 'a dupliqué';
  return 'a effectué une action';
}

function buildSentence(log) {
  const actor = userLabel(log?.user);
  const verb = baseActionPhrase(log?.action_key);
  const moduleKey = String(log?.module_key || '');
  const object = MODULE_OBJECTS[moduleKey] || '';

  if (String(log?.action_key || '') === 'update_modules') {
    const added = Array.isArray(log?.data?.added_modules) ? log.data.added_modules.length : 0;
    const removed = Array.isArray(log?.data?.removed_modules) ? log.data.removed_modules.length : 0;
    if (added || removed) {
      return `${actor} ${verb} (+${added} / -${removed})`;
    }
    return `${actor} ${verb}`;
  }

  if (object && (verb === 'a créé' || verb === 'a modifié' || verb === 'a supprimé' || verb === 'a activé' || verb === 'a désactivé' || verb === 'a publié' || verb === 'a archivé' || verb === 'a dupliqué' || verb === 'a exécuté')) {
    return `${actor} ${verb} ${object}`;
  }

  return `${actor} ${verb} dans ${moduleLabel(moduleKey)}`;
}

function formatDate(v) {
  const raw = String(v || '').trim();
  if (!raw) return '—';
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return raw;
  return dt.toLocaleString('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function AdminLogsPage({ sites = [] }) {
  const [state, setState] = useState({ status: 'loading', logs: [], error: '', nextBeforeId: null, totalCount: 0 });
  const [filters, setFilters] = useState({ site_id: '', module_key: '', q: '' });
  const [loadingMore, setLoadingMore] = useState(false);

  const siteOptions = useMemo(() => {
    const list = Array.isArray(sites) ? [...sites] : [];
    return list.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'fr'));
  }, [sites]);

  const moduleOptions = useMemo(() => {
    const set = new Set();
    for (const l of state.logs || []) {
      const k = String(l?.module_key || '').trim();
      if (k) set.add(k);
    }
    const fromLogs = Array.from(set);
    const fromMap = Object.keys(MODULE_LABELS);
    return Array.from(new Set([...fromLogs, ...fromMap])).sort((a, b) => moduleLabel(a).localeCompare(moduleLabel(b), 'fr'));
  }, [state.logs]);

  function fetchLogs({ append = false, beforeId = null } = {}) {
    if (!append) setState((s) => ({ ...s, status: 'loading', error: '' }));
    else setLoadingMore(true);

    const payload = {
      limit: 120,
      site_id: filters.site_id ? Number(filters.site_id) : undefined,
      module_key: filters.module_key || undefined,
      q: filters.q || undefined,
      before_id: beforeId || undefined,
    };

    api
      .adminLogs(payload)
      .then((res) => {
        if (!res.ok) {
          setState((s) => ({ ...s, status: 'error', error: res.error || 'Chargement impossible' }));
          setLoadingMore(false);
          return;
        }
        const nextLogs = Array.isArray(res.logs) ? res.logs : [];
        if (append) {
          setState((s) => ({
            ...s,
            status: 'ready',
            logs: [...(s.logs || []), ...nextLogs],
            nextBeforeId: res.next_before_id || null,
            totalCount: Number(res.total_count || 0),
            error: '',
          }));
          setLoadingMore(false);
          return;
        }
        setState({ status: 'ready', logs: nextLogs, nextBeforeId: res.next_before_id || null, totalCount: Number(res.total_count || 0), error: '' });
      })
      .catch(() => {
        setState((s) => ({ ...s, status: 'error', error: 'Chargement impossible' }));
        setLoadingMore(false);
      });
  }

  useEffect(() => {
    fetchLogs({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyFilters() {
    fetchLogs({ append: false });
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb items={[{ label: 'Logs' }]} />
        </div>
      </header>

      <div className="Card" style={{ marginBottom: 12 }}>
        <div className="FormGrid" style={{ alignItems: 'end' }}>
          <div className="Field">
            <label className="Label">Site</label>
            <select className="Select" value={filters.site_id} onChange={(e) => setFilters((f) => ({ ...f, site_id: e.target.value }))}>
              <option value="">Tous</option>
              {siteOptions.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="Field">
            <label className="Label">Module</label>
            <select className="Select" value={filters.module_key} onChange={(e) => setFilters((f) => ({ ...f, module_key: e.target.value }))}>
              <option value="">Tous</option>
              {moduleOptions.map((k) => (
                <option key={k} value={k}>
                  {moduleLabel(k)}
                </option>
              ))}
            </select>
          </div>
          <div className="Field">
            <label className="Label">Recherche</label>
            <input
              className="Input"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              placeholder="Projet, utilisateur, action..."
            />
          </div>
        </div>
        <div className="Row" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
          <button type="button" className="Button" onClick={applyFilters}>
            Filtrer
          </button>
        </div>
      </div>

      {state.error ? (
        <div className="Card">
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      {state.status === 'loading' ? (
        <div className="Card" role="status" aria-live="polite">
          Chargement…
        </div>
      ) : null}

      {state.status === 'ready' ? (
        <div className="List" role="list">
          {(state.logs || []).map((l) => {
            const modKey = String(l?.module_key || '');
            const accent = moduleColor(modKey);
            const entityLabel = String(l?.entity?.label || '').trim();
            const entityRoute = String(l?.entity?.route || '').trim();
            const hasEntityLink = entityLabel && entityRoute;
            const sentence = buildSentence(l);
            return (
              <div key={l.id} className="ListItem" role="listitem">
                <div style={{ minWidth: 0 }}>
                  <div className="Badges" style={{ justifyContent: 'flex-start', marginBottom: 6 }}>
                    <span className="Badge" style={{ borderColor: accent, color: accent }}>
                      {moduleLabel(modKey)}
                    </span>
                    <span className="Badge">{formatDate(l.created_at)}</span>
                    {l?.site?.name ? <span className="Badge">{l.site.name}</span> : null}
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {sentence}
                    {hasEntityLink ? (
                      <>
                        {' '}
                        :{' '}
                        <a href={`#${entityRoute}`} className="LinkInline">
                          {entityLabel}
                        </a>
                      </>
                    ) : entityLabel ? (
                      <>
                        {' '}
                        : <span>{entityLabel}</span>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}

          {(state.logs || []).length === 0 ? (
            <div className="Muted">
              {state.totalCount > 0 ? 'Aucun log ne correspond aux filtres actuels.' : 'Aucune action enregistrée.'}
            </div>
          ) : null}
        </div>
      ) : null}

      {state.status === 'ready' && state.nextBeforeId ? (
        <div className="Row" style={{ marginTop: 12, justifyContent: 'center' }}>
          <button
            type="button"
            className="Button Button--ghost"
            disabled={loadingMore}
            onClick={() => fetchLogs({ append: true, beforeId: state.nextBeforeId })}
          >
            {loadingMore ? 'Chargement…' : 'Voir plus'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
